import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ceederoApi } from "@/api/client";

/** One installation, as the API reports it. Polar owns this list. */
export interface Device {
  activatedAt: string;
  activationId: string;
  label: string | null;
  platform: string | null;
}

export interface DeviceList {
  deviceLimit: number | null;
  devices: Device[];
}

export const devicesQueryKey = ["devices"] as const;

/**
 * Read live on every visit rather than cached for long.
 *
 * The API reads Polar directly because Polar owns the list, so a stale copy
 * here would disagree with what the customer sees in Polar's own portal — which
 * is the duplicate-list problem the API refused to create, reappearing in the
 * client.
 */
export const useDevices = () =>
  useQuery({
    queryKey: devicesQueryKey,
    queryFn: () => ceederoApi.request<DeviceList>({ path: "/v1/me/devices" }),
    staleTime: 0,
  });

export const useRemoveDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (activationId: string) =>
      ceederoApi.request<void>({
        method: "DELETE",
        path: `/v1/me/devices/${encodeURIComponent(activationId)}`,
      }),
    // Refetched rather than removed locally: Polar is the authority on what the
    // list now contains, including the limit it counts against.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: devicesQueryKey }),
  });
};
